import { Button } from "@/components/ui/button"
import { Apple, Play, Search, ArrowRight, Sparkles } from "lucide-react"
import { useState } from "react"
import heroBg from "@/assets/hero.jpg"

export function Hero() {
  const [searchValue, setSearchValue] = useState("")

  return (
    <section className="relative overflow-hidden min-h-screen flex items-center">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt="Hero background"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#75055B]/50 via-[#75055B]/40 to-black/60" />
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#DB4789]/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#DB4789]/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-[#DB4789]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }} />
      </div>

      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-40" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-[#DB4789]" />
            <span className="text-sm font-medium text-white">Your Stay, Your Way, Your Price</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
            Book Everything You Need
            <br />
            <span className="bg-gradient-to-r from-white via-[#DB4789] to-white bg-clip-text text-transparent animate-gradient">
              At Your Price
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-xl sm:text-2xl md:text-3xl text-white/90 mb-4 font-semibold max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            Set your budget. Get offers. Save up to 30%.
          </p>

          <p className="text-base sm:text-lg text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
            Accommodation, safaris, dining, nightlife, beauty services, and transport — all with negotiable pricing from verified providers.
          </p>

          {/* Search Bar */}
          <div className="max-w-3xl mx-auto mb-10 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
            <div className="flex flex-col sm:flex-row gap-3 bg-white/95 backdrop-blur-md rounded-2xl p-2 shadow-2xl border border-white/20">
              <div className="flex-1 flex items-center px-4 py-3">
                <Search className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search for service, destination, or date..."
                  className="flex-1 outline-none text-gray-700 placeholder:text-gray-400 bg-transparent text-sm sm:text-base"
                />
              </div>
              <Button 
                className="bg-[#75055B] text-white hover:bg-[#DB4789] font-semibold px-8 py-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-[#DB4789]/30 group"
              >
                Browse Services
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
            <Button
              size="lg"
              className="bg-white text-[#75055B] hover:bg-[#DB4789] hover:text-white font-semibold px-8 py-6 text-base sm:text-lg rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-[#DB4789]/30 transition-all duration-300 w-full sm:w-auto group"
            >
              <Apple className="w-5 h-5 sm:w-6 sm:h-6 mr-2 group-hover:scale-110 transition-transform" />
              Download for iOS
            </Button>
            <Button
              size="lg"
              className="bg-white/10 backdrop-blur-md text-white border-2 border-white/30 hover:bg-white/20 hover:border-white/50 font-semibold px-8 py-6 text-base sm:text-lg rounded-2xl shadow-xl transition-all duration-300 w-full sm:w-auto group"
            >
              <Play className="w-5 h-5 sm:w-6 sm:h-6 mr-2 group-hover:scale-110 transition-transform" />
              Download for Android
            </Button>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: "0.5s" }}>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">2M+</div>
              <div className="text-sm text-white/70">Happy Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">30%</div>
              <div className="text-sm text-white/70">Average Savings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">4.8/5</div>
              <div className="text-sm text-white/70">User Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  )
}
